import React from 'react';
import { Bell, User, LogOut, BookOpen, HelpCircle, UserIcon, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useSidebar } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import NotificationIcon from './NotificationIcon';

interface DashboardTopNavProps {
  pageTitle: string;
}

const DashboardTopNav: React.FC<DashboardTopNavProps> = ({ pageTitle }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { setOpenMobile, isMobile } = useSidebar();
  const userName = user?.user_metadata?.full_name || user?.email || 'User';

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <div className="bg-white border-b shadow-sm animate-fade-in fixed top-0 left-0 right-0 z-50">
      <div className="flex items-center justify-between px-4 sm:px-6 py-2 sm:py-3">
        <div className="flex items-center space-x-2 sm:space-x-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenMobile(true)}
              className="mr-2"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <Link to="/" className="transition-transform duration-200 hover:scale-105">
            <img 
              src="/brand-assets/Sigma%20Pl%20-%20Black.png" 
              alt="Sigma Immigration Logo" 
              className="h-8 sm:h-10 w-auto animate-scale-in"
            />
          </Link>
          <div className="hidden sm:block">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 animate-slide-in-right font-alumni">
              {pageTitle}
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 font-hind">Welcome back, {userName}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-4">
          <NotificationIcon />
          
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="hidden sm:flex">
                <User className="h-4 w-4 mr-2" />
                {userName}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/personal-info')}>
                <UserIcon className="mr-2 h-4 w-4" />
                Your Profile
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/knowledge-base')}>
                <BookOpen className="mr-2 h-4 w-4" />
                Knowledge Base
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/help-center')}>
                <HelpCircle className="mr-2 h-4 w-4" />
                Help Center
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="sm:hidden w-9 h-9 p-0">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/personal-info')}>
                <UserIcon className="mr-2 h-4 w-4" />
                Your Profile
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/knowledge-base')}>
                <BookOpen className="mr-2 h-4 w-4" />
                Knowledge Base
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/help-center')}>
                <HelpCircle className="mr-2 h-4 w-4" />
                Help Center
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="sm:hidden px-4 pb-2">
        <h1 className="text-lg font-bold text-gray-900 font-alumni">
          {pageTitle}
        </h1>
      </div>
    </div>
  );
};

export default DashboardTopNav;
